import './style.css';
import './pages/reels.css';

import {renderApiError} from './components/error.ts';
import {ResourceStar} from './components/wc/resource-star.ts';
import {isAuthenticated} from './lib/auth.ts';
import {LoadResourceError} from './lib/errors.ts';
import {
	clearAuthToken,
	getAuthToken,
	getClientId,
	getLatestProductType,
	getLatestResourceId,
	getLatestResourceType,
	refreshToken,
	setAuthToken,
	setLatestProductType,
	setLatestResourceId,
	setLatestResourceType,
	setLoginRedirectTo
} from './lib/storage.ts';
import {Store} from './lib/store.ts';
import * as EditionFactory from './models/edition-factory.ts';
import {renderIndexPage} from './pages';
import {renderContentSourcePage, renderContentSourcesPage} from './pages/content-sources.ts';
import {renderAdminPage} from './pages/admin.ts';
import {setAutoSummary, setLanguage} from './lib/i18n.ts';
import {renderLoginPage} from './pages/login.ts';
import {renderDigitalProductPage, renderPrintProductPage} from './pages/product.ts';
import {renderReaderPage} from './pages/reader/reader.ts';
import {renderReelsView} from './pages/reels.ts';
import {
	loadAdminConfig,
	loadContentSource,
	loadContentSources,
	loadDigitalEdition,
	loadDigitalEditionPositions,
	loadPrintEdition,
	loadProduct,
	loadRecentDigitalEditionsForProduct,
	loadRecentEditionsForProduct
} from './services/content.ts';
import type {ApiResponse} from './services/http.ts';

// @ts-expect-error
const isTestMode = import.meta.env.VITE_IS_TEST === 'true';

customElements.define('resource-star', ResourceStar);

let adminConfigLoaded = false;

/**
 * Lazy-loads admin config and configures i18n settings.
 * Only loads once per session.
 */
async function ensureAdminConfig(): Promise<void> {
	if (adminConfigLoaded) {
		return;
	}
	adminConfigLoaded = true;

	const adminConfig = await loadAdminConfig();
	if (adminConfig?.language) {
		setLanguage(adminConfig.language);
	}
	if (adminConfig?.autoSummary !== undefined) {
		setAutoSummary(adminConfig.autoSummary);
	}
}

await run();

async function run() {
	if (!window.location.hostname.includes('localhost')) {
		await registerServiceWorker();
	}

	const appEl = document.getElementById('app') as HTMLElement;
	const clientId = getClientId();
	document.title = clientId;

	appEl.dataset.clientId = clientId;

	// Skip authentication in test mode. This doesn't grant access to anything, it just skips redirecting to login.
	// TODO: Consider injecting a token instead
	if (!isTestMode) {
		const redirectTo = await handleAuthentication();
		if (redirectTo) {
			window.location.pathname = redirectTo;
			return;
		}
	}

	// Load admin config preferences
	const adminConfig = await loadAdminConfig();
	if (adminConfig?.language) {
		setLanguage(adminConfig.language);
	}
	if (adminConfig?.autoSummary !== undefined) {
		setAutoSummary(adminConfig.autoSummary);
	}

	const data = await getResourceData(document.location.pathname.trim());
	const params = new URLSearchParams(document.location.search);

	await render(data, params, appEl);
}

async function handleAuthentication(): Promise<string> {
	if (!isAuthenticated() && window.location.pathname !== '/login') {
		setLoginRedirectTo(window.location.href);
		return '/login';
	}

	if (isAuthenticated() && window.location.pathname === '/login') {
		return '/';
	}

	if (isAuthenticated()) {
		const newToken = await refreshToken(getAuthToken(), clearAuthToken);
		if (newToken) {
			setAuthToken(newToken);
		}
	}

	return '';
}

async function registerServiceWorker() {
	if ('serviceWorker' in navigator) {
		try {
			const registration = await navigator.serviceWorker.register('/sw.js', {
				scope: '/'
			});
			if (registration.installing) {
				console.log('Service worker installing');
			} else if (registration.waiting) {
				console.log('Service worker installed');
			} else if (registration.active) {
				console.log('Service worker active');
			}
		} catch (error) {
			console.error(`Registration failed with ${error}`);
		}
	}
}

async function getResourceData(pathname: string) {
	const [, resourceTypeFromPath, resourceId, subResource, pageNumber] = pathname.split('/');

	const data = {
		resourceType: resourceTypeFromPath,
		resourceId,
		subResource,
		pageNumber
	};

	const latestResourceId = getLatestResourceId();
	const latestResourceType = getLatestResourceType();

	if (!resourceTypeFromPath && latestResourceType && latestResourceId) {
		data.resourceType = latestResourceType;
		switch (latestResourceType) {
			case 'reader': {
				const latestEditions = await loadRecentEditionsForProduct(latestResourceId, 1);
				if (latestEditions.status === 200 && latestEditions.body?.[0]) {
					data.resourceId = latestEditions.body[0]._id;
				}
				break;
			}
			case 'digital-reader': {
				const productType = getLatestProductType();
				if (productType === 'frontpage') {
					data.resourceId = latestResourceId;
					break;
				}

				const latestEditions = await loadRecentDigitalEditionsForProduct(latestResourceId, 1);
				if (latestEditions.status === 200 && latestEditions.body?.[0]) {
					data.resourceId = latestEditions.body[0].id;
				}
				break;
			}
			case 'feed': {
				if (latestResourceId) {
					data.resourceId = latestResourceId;
				}
				break;
			}
			case 'reels': {
				if (latestResourceId) {
					data.resourceId = latestResourceId;
				}
				break;
			}
		}
	}

	return data;
}

async function render(
	data: {resourceType: string; resourceId: string; subResource: string; pageNumber: string},
	params: URLSearchParams,
	appEl: HTMLElement
) {
	const {resourceType, resourceId, subResource, pageNumber} = data;
	const clientId = getClientId();
	const store = await Store.load();

	if (subResource === 'undefined') {
		return;
	}

	try {
		switch (resourceType) {
			case 'admin':
				return showAdminPage(appEl);
			case 'login':
				return showLoginPage(appEl, clientId);
			case 'home':
				return showHomePage(appEl);
			case 'reels':
				return showReelsPage(appEl, resourceId);
			case 'print-products':
				if (resourceId) {
					return await showPrintProductPage(appEl, resourceId);
				}
				break;
			case 'front-pages':
			case 'digital-products':
				if (resourceId) {
					return await showDigitalProductPage(appEl, resourceId);
				}
				break;
			case 'reader':
				if (resourceId) {
					return await showPrintReaderPage(appEl, resourceId, subResource, pageNumber);
				}
				break;
			case 'digital-reader':
				if (resourceId) {
					return await showDigitalReaderPage(appEl, resourceId, subResource, pageNumber);
				}
				break;
			case 'reels-edition':
				if (resourceId) {
					return await showReelsEditionPage(appEl, resourceId, subResource, pageNumber);
				}
				break;
			case 'feed': {
				if (resourceId) {
					return await showFeedReaderPage(appEl, resourceId, subResource, pageNumber, params);
				}
				return await showContentSourcesPage(appEl);
			}
			case 'article':
				if (resourceId && subResource) {
					return await showSingleArticlePage(appEl, resourceId, subResource, pageNumber, store);
				}
				// no singleItemSourceId provided, use the default from the adapter
				return await showSingleArticlePage(appEl, undefined, resourceId, pageNumber, store);
			case 'pressflow':
				if (resourceId && subResource) {
					return await showPressflowPage(appEl, resourceId, subResource, pageNumber, store);
				}
				break;
		}
	} catch (err) {
		if (err instanceof LoadResourceError) {
			if (err.response.status === 401) {
				clearAuthToken();
				setLoginRedirectTo(window.location.href);
				window.location.pathname = '/login';
				return;
			}
			appEl.innerHTML = renderApiError(err.response.body, 'Error loading product');
			return;
		}

		if (err instanceof Error) {
			console.log(err);
			appEl.innerHTML = `An error occured: ${err.message}`;
			return;
		}
	}

	if (isTestMode) {
		console.log('TESTMODE: Skip home page');
		return;
	}

	return showHomePage(appEl);
}

function showAdminPage(appEl: HTMLElement) {
	appEl.dataset.page = 'admin';
	return renderAdminPage(appEl);
}

function showLoginPage(appEl: HTMLElement, clientId: string) {
	appEl.dataset.page = 'login';
	return renderLoginPage(appEl, clientId);
}

function showHomePage(appEl: HTMLElement) {
	appEl.dataset.page = 'index';
	return renderIndexPage(appEl);
}

function showReelsPage(appEl: HTMLElement, videoId: string | undefined) {
	appEl.dataset.page = 'reels-view';
	return renderReelsView(appEl, videoId);
}

async function showPrintProductPage(appEl: HTMLElement, resourceId: string) {
	appEl.dataset.page = 'product-view';
	const productData = await loadAndCheck(() => loadProduct(resourceId), 'product');
	return renderPrintProductPage(appEl, productData);
}

async function showDigitalProductPage(appEl: HTMLElement, resourceId: string) {
	appEl.dataset.page = 'product-view';
	const productData = await loadAndCheck(() => loadProduct(resourceId), 'product');
	return renderDigitalProductPage(appEl, productData);
}

async function showPrintReaderPage(appEl: HTMLElement, resourceId: string, subResource: string, pageNumberStr: string) {
	await ensureAdminConfig();
	setLatestResourceType('reader');
	appEl.dataset.page = 'reader-view';
	appEl.dataset.status = 'loading';

	const printEditionData = await loadAndCheck(() => loadPrintEdition(resourceId), 'print-edition');
	const productData = await loadAndCheck(() => loadProduct(printEditionData.product), 'product');
	setLatestResourceId(productData.attributes.name);

	const readerEdition = await EditionFactory.fromPrintEdition(printEditionData, productData);
	document.title = readerEdition.title;
	const pageNumber = Number(pageNumberStr) || 1;
	return renderReaderPage(appEl, readerEdition, 'reader', subResource, pageNumber);
}

async function showDigitalReaderPage(
	appEl: HTMLElement,
	resourceId: string,
	subResource: string,
	pageNumberStr: string
) {
	await ensureAdminConfig();
	setLatestResourceType('digital-reader');
	appEl.dataset.page = 'reader-view';
	appEl.dataset.status = 'loading';

	const digitalEditionData = await loadAndCheck(() => loadDigitalEdition(resourceId), 'digital-edition');
	const digitalEditionPositionsData = await loadAndCheck(
		() => loadDigitalEditionPositions(resourceId),
		'digital-edition-positions'
	);
	const productData = await loadAndCheck(
		() => loadProduct(digitalEditionData.relationships.products.data.id),
		'product'
	);

	setLatestProductType(productData.attributes.productType);
	if (productData.attributes.productType === 'frontpage') {
		setLatestResourceId(resourceId);
	} else {
		setLatestResourceId(productData.attributes.name);
	}

	const readerEdition = await EditionFactory.fromDigitalEdition(
		digitalEditionData,
		digitalEditionPositionsData,
		productData
	);
	document.title = readerEdition.title;
	const pageNumber = Number(pageNumberStr) || 1;
	return renderReaderPage(appEl, readerEdition, 'digital-reader', subResource, pageNumber);
}

async function showReelsEditionPage(
	appEl: HTMLElement,
	resourceId: string,
	subResource: string,
	pageNumberStr: string
) {
	await ensureAdminConfig();
	setLatestResourceType('reels');
	setLatestResourceId(resourceId);
	appEl.dataset.status = 'loading';

	appEl.dataset.page = 'reader-view';
	const readerEdition = await EditionFactory.fromPlaylist(resourceId);
	document.title = readerEdition.productTitle;
	const pageNumber = Number(pageNumberStr) || 1;
	return renderReaderPage(appEl, readerEdition, 'reels', subResource, pageNumber);
}

async function showFeedReaderPage(
	appEl: HTMLElement,
	resourceId: string,
	subResource: string,
	pageNumberStr: string,
	params: URLSearchParams
) {
	await ensureAdminConfig();
	setLatestResourceType('feed');
	setLatestResourceId(resourceId);
	appEl.dataset.status = 'loading';
	const [source, query] = resourceId.split('-');

	if (!query) {
		const contentSource = await loadAndCheck(() => loadContentSource(source), 'content-source');
		return renderContentSourcePage(appEl, contentSource);
	}

	appEl.dataset.page = 'reader-view';
	const now = new Date();
	const date = params.get('date') || now.toISOString().substring(0, 10);
	const tag = params.get('tag') || '';
	const articleId = params.get('articleId') || '';
	const readerEdition = await EditionFactory.fromContentSource(source, query, date, tag, articleId);
	document.title = readerEdition.productTitle;
	const pageNumber = Number(pageNumberStr) || 1;
	return renderReaderPage(appEl, readerEdition, 'feed', subResource, pageNumber);
}

async function showSingleArticlePage(
	appEl: HTMLElement,
	sourceId: string | undefined,
	articleId: string,
	pageNumber: string,
	store: Store
) {
	await ensureAdminConfig();
	appEl.dataset.page = 'reader-view';
	appEl.dataset.status = 'loading';

	let data = '';
	if (sourceId === 'fixture') {
		data = sessionStorage.getItem('test-article') || '';
		if (!data) {
			throw Error('Unable to load fixture data for "test-article"');
		}
	}

	const readerEdition = await EditionFactory.fromSingleArticle(sourceId, articleId, store, data);

	return renderReaderPage(appEl, readerEdition, 'article', articleId, Number(pageNumber) || 1);
}

async function showPressflowPage(
	appEl: HTMLElement,
	sourceId: string | undefined,
	articleId: string,
	pageNumber: string,
	store: Store
) {
	await ensureAdminConfig();
	appEl.dataset.page = 'reader-view';
	appEl.dataset.mode = 'pressflow';
	appEl.dataset.status = 'loading';

	let data = '';
	if (sourceId === 'fixture') {
		data = sessionStorage.getItem('test-article') || '';
	}

	const readerEdition = await EditionFactory.fromSingleArticle(sourceId, articleId, store, data);

	return renderReaderPage(appEl, readerEdition, 'pressflow', articleId, Number(pageNumber) || 1);
}

async function showContentSourcesPage(appEl: HTMLElement) {
	const contentSourcesData = await loadAndCheck(() => loadContentSources(), 'content-sources');
	return renderContentSourcesPage(appEl, contentSourcesData);
}

async function loadAndCheck<T>(loader: () => Promise<ApiResponse<T>>, type: string): Promise<T> {
	const response = await loader();
	if (response.status !== 200) {
		throw new LoadResourceError(`Error loading ${type}`, response);
	}

	return response.body;
}
